/**
 * The avatar menu (spec M0-F4 §7.7).
 *
 * Two columns plus a Working-status footer, in monday's order
 * (`[ui:library/shell-05-profile-menu.png]`). Ours keeps only the entries that map to something
 * we build; monday's App marketplace, Mobile app, monday.labs and Upgrade account are simply
 * absent (§3.2). Items whose module has not shipped are rendered **disabled**, not hidden, so
 * the menu's shape stays stable — hiding them would make the two columns jump per milestone.
 */
import { AnchoredPopover, Avatar, BrandLogo, PRODUCT_NAME, RadioButton } from '@elizmonday/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTRPCClient } from '../lib/trpc';
import type { ShellCapabilities } from '../modules/shell/bootstrap';
import { performLogout } from '../modules/shell/logout';
import { usePreference, type ThemeName } from '../modules/shell/preferences';
import { useShellStore } from '../modules/shell/store';

export interface ProfileMenuUser {
  id: string;
  name: string | null;
  email: string | null;
  photoUrl: string | null;
  theme: ThemeName;
}

function initials(name: string | null, email: string | null): string {
  const source = name ?? email ?? '?';
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  return (parts[0]?.[0] ?? '?').toUpperCase() + (parts[1]?.[0] ?? '').toUpperCase();
}

interface ItemProps {
  label: string;
  to?: string;
  onClick?: () => void;
  disabled?: boolean;
  /** Why it is disabled, e.g. "M11". Rendered as the `title`, never as visible chrome. */
  reason?: string;
  /**
   * **P-163.** Required, and written at the call site as the id it becomes on the page — the
   * ledger's static parser reads it there (round 10l taught it to follow a component's own
   * naming prop), so the eleven rows are eleven countable rows instead of a single dynamic
   * element literally called `testId`. A row with no id is a row nothing can name.
   */
  testId: string;
  /**
   * P-038. Every entry closes the menu, the links included. A `<Link>` used to navigate with
   * the popover left open on top of the screen it had just opened — the menu is a menu, not a
   * panel, and monday closes it on any choice.
   */
  onAct?: () => void;
}

function Item({ label, to, onClick, disabled, reason, testId, onAct }: ItemProps) {
  if (to && !disabled) {
    return (
      <Link className="ez-profile-menu__item" to={to} onClick={() => onAct?.()} data-testid={testId}>
        {label}
      </Link>
    );
  }
  return (
    <button
      type="button"
      className="ez-profile-menu__item"
      onClick={() => {
        onAct?.();
        onClick?.();
      }}
      disabled={disabled}
      title={disabled ? reason : undefined}
      data-testid={testId}
    >
      {label}
    </button>
  );
}

export interface ProfileMenuProps {
  user: ProfileMenuUser | null;
  accountName: string | null;
  capabilities: ShellCapabilities;
}

const THEMES: { id: ThemeName; label: string; enabled: boolean }[] = [
  { id: 'light', label: 'Light', enabled: true },
  // Dark and black ship with M8.P2; the preference is stored either way (F7 §3.2, E7).
  { id: 'dark', label: 'Dark', enabled: false },
  { id: 'black', label: 'Black', enabled: false },
];

export function ProfileMenu({ user, accountName, capabilities }: ProfileMenuProps) {
  const [open, setOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  /** P-005: a body portal, so the menu is never trapped in the top bar's stacking context. */
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [theme, setTheme] = usePreference('shell.theme');
  const [doNotDisturb, setDoNotDisturb] = usePreference('status.doNotDisturb');
  const openOverlay = useShellStore((state) => state.openOverlay);
  const client = useTRPCClient();
  const queryClient = useQueryClient();
  /** P-038: a second click while the first logout is in flight revokes nothing twice. */
  const loggingOut = useRef(false);

  const close = () => setOpen(false);

  const content = (
    <div className="ez-profile-menu" role="menu" aria-label="Account menu" data-testid="profile-menu">
      <div className="ez-profile-menu__header">
        <BrandLogo size={32} accountName={accountName} />
        <strong>{accountName ?? PRODUCT_NAME}</strong>
      </div>

      <div className="ez-profile-menu__columns">
        <div>
          <div className="ez-profile-menu__column-title">Account</div>
          <Item label="My profile" to="/me/profile" onAct={close} testId="profile-my-profile" />
          <Item label="Import data" disabled reason="Arrives with M12" testId="profile-import" />
          <Item label="Developers" to="/me/developers" onAct={close} testId="profile-developers" />
          {capabilities.trash ? <Item label="Trash" to="/trash" onAct={close} testId="profile-trash" /> : null}
          {capabilities.archive ? (
            <Item label="Archive" to="/archive" onAct={close} testId="profile-archive" />
          ) : null}
          {capabilities.administration ? (
            <Item label="Administration" to="/admin/general" onAct={close} testId="profile-admin" />
          ) : null}
          {capabilities.teams ? (
            <Item label="Teams" to="/admin/teams" onAct={close} testId="profile-teams" />
          ) : null}
          {/*
            * P-038. **Not** a `<Link>`. It was one, to `/logout`, and `/logout` was not in the
            * route table — the click landed on the shell's `*` NotFound with the session intact.
            * The row now revokes the session itself and only then leaves for `/login`, so it
            * works whatever the router is doing; `/logout` is a real route as well, for the URL
            * people type and bookmark, and both run the same helper.
            */}
          <Item
            label="Log out"
            onAct={close}
            onClick={() => {
              if (loggingOut.current) return;
              loggingOut.current = true;
              void performLogout(client, queryClient);
            }}
            testId="profile-logout"
          />
        </div>

        <div>
          <div className="ez-profile-menu__column-title">Explore</div>
          <Item
            label="Shortcuts"
            onAct={close}
            onClick={() => openOverlay('shortcuts')}
            testId="profile-shortcuts"
          />
          <div className="ez-profile-menu__separator" role="separator" />
          {capabilities.inviteMembers ? (
            <Item
              label="Invite members"
              onAct={close}
              onClick={() => openOverlay('invite')}
              testId="profile-invite"
            />
          ) : null}
          <Item label="Get help" to="/help" onAct={close} testId="profile-help" />

          <button
            type="button"
            className="ez-profile-menu__item"
            aria-expanded={themeOpen}
            aria-haspopup="menu"
            onClick={() => setThemeOpen((value) => !value)}
            data-testid="profile-change-theme"
          >
            Change theme ›
          </button>
          {themeOpen ? (
            <div role="menu" aria-label="Change theme" data-testid="theme-submenu">
              {THEMES.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={theme === option.id}
                  className="ez-profile-menu__item"
                  disabled={!option.enabled}
                  title={option.enabled ? undefined : 'Ships with M8.P2'}
                  onClick={() => setTheme(option.id)}
                  data-testid={`theme-${option.id}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="ez-profile-menu__footer">
        <div className="ez-profile-menu__column-title">Working status</div>
        {/* The observed footer is a real On/Off radio pair, not two buttons (`[ui:shell-05]`). */}
        <div className="ez-profile-menu__status-row" role="radiogroup" aria-label="Do not disturb">
          <span className="ez-profile-menu__status-label">Do not disturb</span>
          <RadioButton
            name="do-not-disturb"
            text="On"
            checked={doNotDisturb}
            onSelect={() => setDoNotDisturb(true)}
            data-testid="dnd-on"
          />
          <RadioButton
            name="do-not-disturb"
            text="Off"
            checked={!doNotDisturb}
            onSelect={() => setDoNotDisturb(false)}
            data-testid="dnd-off"
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="ez-topbar__brand"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={user?.name ? `${user.name} — account menu` : 'Account menu'}
        onClick={() => setOpen((value) => !value)}
        data-testid="topbar-profile"
      >
        <Avatar
          type={user?.photoUrl ? 'img' : 'text'}
          src={user?.photoUrl ?? undefined}
          text={initials(user?.name ?? null, user?.email ?? null)}
          size="medium"
          withoutTooltip
        />
      </button>

      <AnchoredPopover
        anchor={triggerRef.current}
        open={open}
        onClose={() => setOpen(false)}
        label="Account menu"
        align="end"
        data-testid="profile-menu-popover"
      >
        {content}
      </AnchoredPopover>
    </>
  );
}
